import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Lock, Phone } from "lucide-react";
import { INTEREST_OPTIONS, PHONE, PHONE_HREF, STATES } from "./data/content";
import { captureUTM, trackEvent } from "../lib/tracking";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", mobile: "", email: "", state: "VIC", role: "",
  interest: "", message: "", consent: false,
};

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const prefill = (e) => setForm((f) => ({ ...f, message: e.detail }));
    window.addEventListener("ahq-prefill", prefill);
    return () => window.removeEventListener("ahq-prefill", prefill);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your name";
    if (form.mobile.trim().length < 6) er.mobile = "Please enter your mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email";
    if (form.role.trim().length < 2) er.role = "Tell us your current role or business";
    if (!form.interest) er.interest = "Choose the option that fits best";
    if (!form.consent) er.consent = "Please accept the privacy consent to continue";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, utm: captureUTM() }),
      });
      if (!res.ok) throw new Error("failed");
      setDone(true);
      trackEvent("form_submit", { interest: form.interest, state: form.state });
    } catch (err) {
      setErrors({ server: "Something went wrong sending your enquiry. Please call us — it will still be handled in confidence." });
    } finally {
      setSending(false);
    }
  };

  const inputCls = (er) =>
    `w-full rounded-xl border bg-ink/60 px-4 py-3.5 text-sm text-white placeholder:text-slate-600 transition-colors duration-200 ${
      er ? "border-red-500/70" : "border-white/10 focus:border-gold/60"
    }`;

  if (done) {
    return (
      <section id="contact" className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl rounded-3xl border border-gold/40 bg-panel/70 p-8 text-center sm:p-12" data-testid="contact-success-panel">
            <CheckCircle2 className="mx-auto h-12 w-12 text-gold-light" />
            <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Thank you, {form.name.split(" ")[0]}.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">
              Your confidential enquiry has been received. A member of the Agency HQ team will be in touch
              personally — and only ever on the details you gave us.
            </p>
            <p className="mt-8 label-mono">Prefer to talk now?</p>
            <a
              href={PHONE_HREF}
              data-testid="contact-success-call-btn"
              className="mt-3 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-3.5 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/25 transition-transform duration-200 active:scale-95"
            >
              <Phone className="h-4 w-4" />
              Call {PHONE}
            </a>
            <p className="mt-6 text-xs text-slate-600">No obligation. Nothing shared with your current agency.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="label-mono">Confidential enquiry</p>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                Let&rsquo;s have a confidential conversation about your business.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                Tell us a little about where you are now. We&rsquo;ll come back to you personally — no
                sales scripts, no pressure, and nothing shared with your current agency.
              </p>
              <ul className="mt-8 space-y-4 text-sm text-slate-300">
                {[
                  "Seen only by Agency HQ's small recruitment team",
                  "No obligation — and no contact with your current agency",
                  "A real conversation with experienced people",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Lock className="mt-0.5 h-4 w-4 shrink-0 text-gold-light" />
                    {t}
                  </li>
                ))}
              </ul>
              <a href={PHONE_HREF} className="mt-8 inline-block font-mono text-sm text-gold-light hover:text-white" data-testid="contact-phone-link">
                Or call {PHONE}
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.08}>
              <form
                onSubmit={submit}
                noValidate
                className="rounded-3xl border border-white/10 bg-panel/60 p-6 sm:p-9"
                data-testid="contact-form"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="f-name" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Name</label>
                    <input id="f-name" data-testid="contact-input-name" className={inputCls(errors.name)} placeholder="Jane Smith" value={form.name} onChange={set("name")} />
                    {errors.name && <p className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="f-mobile" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Mobile</label>
                    <input id="f-mobile" data-testid="contact-input-mobile" type="tel" className={inputCls(errors.mobile)} placeholder="04XX XXX XXX" value={form.mobile} onChange={set("mobile")} />
                    {errors.mobile && <p className="mt-1.5 text-xs text-red-400">{errors.mobile}</p>}
                  </div>
                  <div>
                    <label htmlFor="f-email" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Email</label>
                    <input id="f-email" data-testid="contact-input-email" type="email" className={inputCls(errors.email)} placeholder="jane@yourbrand.com.au" value={form.email} onChange={set("email")} />
                    {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="f-state" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">State</label>
                    <select id="f-state" data-testid="contact-select-state" className={inputCls(false) + " appearance-none"} value={form.state} onChange={set("state")}>
                      {STATES.map((s) => <option key={s} value={s} className="bg-ink">{s}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="f-role" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">Current role or business</label>
                  <input id="f-role" data-testid="contact-input-role" className={inputCls(errors.role)} placeholder="e.g. Sales agent at a franchise, independent agency owner…" value={form.role} onChange={set("role")} />
                  {errors.role && <p className="mt-1.5 text-xs text-red-400">{errors.role}</p>}
                </div>

                <fieldset className="mt-7">
                  <legend className="mb-3 block text-xs font-semibold uppercase tracking-wider text-slate-400">I am interested in</legend>
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    {INTEREST_OPTIONS.map((o) => (
                      <label
                        key={o.value}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3.5 text-sm transition-all duration-200 ${
                          form.interest === o.value
                            ? "border-gold bg-gold/10 text-white"
                            : "border-white/10 text-slate-400 hover:border-white/25 hover:text-white"
                        }`}
                      >
                        <input
                          type="radio"
                          name="interest"
                          value={o.value}
                          checked={form.interest === o.value}
                          onChange={set("interest")}
                          data-testid={`contact-radio-${o.value}`}
                          className="h-4 w-4 accent-amber-500"
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                  {errors.interest && <p className="mt-1.5 text-xs text-red-400">{errors.interest}</p>}
                </fieldset>

                <div className="mt-5">
                  <label htmlFor="f-message" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Message <span className="normal-case text-slate-600">(optional)</span>
                  </label>
                  <textarea id="f-message" data-testid="contact-input-message" rows={4} className={inputCls(false)} placeholder="Anything you'd like us to know before we talk…" value={form.message} onChange={set("message")} />
                </div>

                <label className="mt-6 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-slate-400">
                  <input type="checkbox" checked={form.consent} onChange={set("consent")} data-testid="contact-consent-checkbox" className="mt-0.5 h-4 w-4 accent-amber-500" />
                  <span>
                    I agree that Agency HQ may collect and use these details to respond to my enquiry, in
                    line with its privacy policy. My enquiry is confidential and will not be shared with
                    my current agency.
                  </span>
                </label>
                {errors.consent && <p className="mt-1.5 text-xs text-red-400">{errors.consent}</p>}

                {errors.server && (
                  <p className="mt-5 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300" data-testid="contact-error-alert">
                    {errors.server}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-4 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/25 transition-all duration-200 hover:from-amber-600 hover:to-amber-700 active:scale-[0.98] disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {sending ? "Sending…" : "Book my confidential conversation"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
